import React from 'react';
import PropTypes from 'prop-types';
import IconButton from '@mui/material/IconButton';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import InformationStack from '../stacks/InformationStack';
import { isWithoutRatification } from '../../helpers/ratification-status-translator';
import { useIsMobile, useBooleanState } from '../../hooks';
import useRatificationRestrictions from '../../hooks/useRatificationRestrictions';
import PendingDocumentsDialog from '../dialogs/PendingDocumentsDialog';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import { isInvoiceOperatedOrExposed } from '../../helpers/invoices';
import { EXPOSED_STATUS } from '../../constants/invoices';
import { SUCCESS_STACK_CLASS_NAME, WARNING_STACK_CLASS_NAME } from '../../theme/constants/stack';

const getReadyText = (invoice) => {
  if (EXPOSED_STATUS.includes(invoice?.status)) return 'Girada';
  if (invoice?.status === 'FINISHED') return 'Recaudada';
  return 'Lista para giro';
};

const getStackClassName = (hasRestrictions) => {
  if (!hasRestrictions) return SUCCESS_STACK_CLASS_NAME;
  return WARNING_STACK_CLASS_NAME;
};

const TransferConditionsCell = ({ invoice, type, customDialogTitle, customDialogSubtitle }) => {
  const isMobile = useIsMobile();
  const company = useSelectedCompany();
  const executiveAssigned = company?.executiveAssigned;

  if (['PENDINGDOCUMENTSEXPIRED', 'REJECTEDPREOFFEREVALUATION'].includes(invoice.status)) return null;
  if (
    ['EVALUATING', 'DOCUMENTSREVIEWED'].includes(
      invoice.preoffer?.preofferevaluationrequest?.status,
    )
  ) {
    return null;
  }

  const [openDialog, setOpenDialog] = useBooleanState();
  const filteredRestrictions = invoice.restrictedBy?.filter(
    (res) => ['CREATED', 'PENDING'].includes(res.status)
      && res.restrictionType === type
      && res.restriction !== 'OTHER',
  ) || [];

  const { pendingRestrictions } = useRatificationRestrictions(invoice, type, filteredRestrictions);
  const hasRestrictions = (
    pendingRestrictions.length > 0
    || !isWithoutRatification(
      invoice?.offer?.transferCondition,
      invoice?.isRatificated,
    )
  ) && !isInvoiceOperatedOrExposed(invoice?.status);

  const handleStackClick = (event) => {
    event.stopPropagation();
    setOpenDialog();
  };

  return (
    <>
      <InformationStack
        text={hasRestrictions ? 'Ver requisitos' : getReadyText(invoice)}
        className={getStackClassName(hasRestrictions)}
        variant={isMobile ? 'small_1' : 'small_3'}
        fontWeight={600}
        IconComponent={hasRestrictions ? (
          <IconButton
            onClick={handleStackClick}
            sx={{ p: 0 }}
          >
            <ChevronRightOutlinedIcon sx={{ fontSize: { xs: 18, sm: 24 }, p: 0 }} />
          </IconButton>
        ) : null}
        onClick={hasRestrictions ? handleStackClick : () => {}}
        isClickable={hasRestrictions}
        sx={{
          borderRadius: 16,
          minWidth: 130,
          justifyContent: 'center',
        }}
        iconLast
        spacing={0}
      />

      {openDialog && (
        <PendingDocumentsDialog
          open={openDialog}
          onClose={setOpenDialog}
          type={type}
          filteredRestrictions={pendingRestrictions}
          executiveAssigned={executiveAssigned}
          invoiceId={invoice.id}
          customDialogTitle={customDialogTitle}
          customDialogSubtitle={customDialogSubtitle}
        />
      )}
    </>
  );
};

const detail = PropTypes.shape({
  id: PropTypes.string.isRequired,
  restrictionLabel: PropTypes.string.isRequired,
  description: PropTypes.string,
});

TransferConditionsCell.propTypes = {
  type: PropTypes.oneOf(['Risk', 'Operations']).isRequired,
  customDialogTitle: PropTypes.string,
  customDialogSubtitle: PropTypes.string,
  invoice: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    offer: PropTypes.shape({
      transferCondition: PropTypes.string,
    }),
    reviewDocumentsManager: PropTypes.shape({
      id: PropTypes.string,
      requiresOc: PropTypes.bool,
      requiresHes: PropTypes.bool,
      requiresPaymentStatus: PropTypes.bool,
      requiresPortalScreenshot: PropTypes.bool,
      hasReceptionAcknowledgment: PropTypes.bool,
      ocReviewState: PropTypes.string,
      hesReviewState: PropTypes.string,
      paymentStatusReviewState: PropTypes.string,
      portalScreenshotReviewState: PropTypes.string,
      receptionAcknowledgmentReviewState: PropTypes.string,
      createdAt: PropTypes.string,
    }),
    isRatificated: PropTypes.bool,
    isRevolving: PropTypes.bool,
    company: PropTypes.shape({
      id: PropTypes.string.isRequired,
      executiveAssigned: PropTypes.shape({
        id: PropTypes.string.isRequired,
        completeName: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
        phoneNumber: PropTypes.string.isRequired,
      }),
    }),
    preoffer: PropTypes.shape({
      id: PropTypes.string,
      preofferevaluationrequest: PropTypes.shape({
        id: PropTypes.string,
        status: PropTypes.string,
      }),
    }),
    restrictedBy: PropTypes.arrayOf(detail),
  }).isRequired,
};
TransferConditionsCell.defaultProps = {
  customDialogTitle: null,
  customDialogSubtitle: null,
};

export default TransferConditionsCell;
